import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { HandThumbDownIcon, HandThumbUpIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import {
  BookOpen,
  Briefcase,
  ClipboardCheck,
  GraduationCap,
  HeartPulse,
  MessageSquare,
  ShieldCheck,
  Trophy,
  Users,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { API_URL } from '../config/api';
import { feedService } from '../services/feed.service';
import { Feed } from '../types/feed';
import SEO from '../components/SEO/SEO';

const FEED_PAGE_SIZE = 10;
const HOME_TITLE = 'LocalAlly - Tutors, Coaching, Books and Stationery Near You';
const HOME_DESCRIPTION =
  'Search LocalAlly for nearby tutors, coaching, scholarships, exams, books and stationery across villages, towns, mandals, colonies, neighbourhoods and cities.';
const EXTENSION_NOTICE =
  'Important update: N. Surya Narayana Memorial Scholarship 2026 Talent test registrations have been extended until 31st December 2026. The scholarship exam is scheduled for 10:00 AM on Sunday, 24th January 2027.';
const HOME_SCHEMA = [
  {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'LocalAlly',
    url: 'https://localally.in',
    logo: 'https://localally.in/logo_1.jpeg',
    description:
      'LocalAlly helps students, parents, tutors, local book shops and communities discover trusted nearby education support.',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer support',
      areaServed: 'IN',
      availableLanguage: ['English', 'Telugu'],
    },
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'LocalAlly local service catalog',
      itemListElement: [
        { '@type': 'Offer', name: 'Home tutors and coaching' },
        { '@type': 'Offer', name: 'Books and stationery marketplace' },
        { '@type': 'Offer', name: 'Scholarships, exams, student progress and safety support' },
      ],
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'LocalAlly',
    url: 'https://localally.in',
    description: HOME_DESCRIPTION,
  },
  {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: HOME_TITLE,
    url: 'https://localally.in/',
    description: HOME_DESCRIPTION,
    about: [
      { '@type': 'Thing', name: 'Tutors and coaching near me' },
      { '@type': 'Thing', name: 'Books and stationery near me' },
      { '@type': 'Thing', name: 'Scholarships and exams' },
    ],
  },
];

const HomeSEO: React.FC = () => (
  <SEO title={HOME_TITLE} description={HOME_DESCRIPTION} path="/" schema={HOME_SCHEMA} />
);
const PROMO_BANNERS = [
  {
    id: 'teachers',
    title: 'Find Tutor Near Me',
    subtitle: 'Find nearby tutors for all classes, subjects, boards and exams.',
    bullets: ['Safe booking', 'Nearby experts', 'Secure payments'],
    cta: 'Find Teachers',
    to: '/education/tutors',
    bg: 'from-blue-600 via-blue-500 to-sky-400',
  },
  {
    id: 'scholarship',
    title: 'N. Surya Narayana Memorial Scholarship 2026',
    subtitle: 'A Tribute to a Passionate Teacher. A Step Towards a Brighter Future.',
    bullets: ['Free Registration', 'Merit-Based Selection', 'Scholarship Awards'],
    cta: 'Register Now',
    to: '/scholarship',
    bg: 'from-cyan-600 via-blue-600 to-indigo-600',
  },
  {
    id: 'books-stationery',
    title: 'LocalAlly Books & Stationery',
    subtitle: 'Buy, sell, exchange, donate, request books, and support local book shops.',
    bullets: ['Used books', 'Verified shops', 'Stationery'],
    cta: 'Open Books',
    to: '/books',
    bg: 'from-emerald-600 via-teal-600 to-cyan-500',
  },
  {
    id: 'teach-earn',
    title: 'Teach & Earn with Confidence',
    subtitle: 'Get students, create exams, and grow your teaching income.',
    bullets: ['Verified profile', 'Secure payments', 'Growth support'],
    cta: 'Go to Teacher Page',
    to: '/teacher',
    bg: 'from-amber-500 via-orange-500 to-red-500',
  },
  {
    id: 'jobs',
    title: 'Join the LGC Jobs Program',
    subtitle: 'Part-time jobs, flexible hours, and extra income.',
    bullets: ['Local promoters', 'Exam moderators', 'Online tutors'],
    cta: 'Go to Jobs Page',
    to: '/jobs',
    bg: 'from-teal-600 via-emerald-600 to-green-500',
  },
] as const;
const SERVICE_MODULES: Array<{
  title: string;
  subtitle: string;
  to: string;
  icon: React.ElementType;
  tone: string;
  authRequired?: boolean;
}> = [
  {
    title: 'Find Tutors',
    subtitle: 'Classes, subjects, nearby teachers',
    to: '/education/tutors',
    icon: GraduationCap,
    tone: 'border-cyan-100 bg-cyan-50 text-cyan-700',
  },
  {
    title: 'Scholarship',
    subtitle: 'Register and access exam',
    to: '/scholarship',
    icon: Trophy,
    tone: 'border-amber-100 bg-amber-50 text-amber-700',
  },
  {
    title: 'Books',
    subtitle: 'Buy, sell, exchange, donate',
    to: '/books',
    icon: BookOpen,
    tone: 'border-emerald-100 bg-emerald-50 text-emerald-700',
  },
  {
    title: 'Exams',
    subtitle: 'Practice and public exams',
    to: '/exams',
    icon: ClipboardCheck,
    tone: 'border-indigo-100 bg-indigo-50 text-indigo-700',
  },
  {
    title: 'Teacher',
    subtitle: 'Teach, profile, certification',
    to: '/teacher',
    icon: Users,
    tone: 'border-orange-100 bg-orange-50 text-orange-700',
  },
  {
    title: 'Jobs',
    subtitle: 'Local work and income',
    to: '/jobs',
    icon: Briefcase,
    tone: 'border-teal-100 bg-teal-50 text-teal-700',
  },
  {
    title: 'Safety SOS',
    subtitle: 'Emergency support hub',
    to: '/sos',
    icon: HeartPulse,
    tone: 'border-rose-100 bg-rose-50 text-rose-700',
    authRequired: true,
  },
  {
    title: 'Login',
    subtitle: 'Open your dashboard',
    to: '/login',
    icon: ShieldCheck,
    tone: 'border-slate-200 bg-slate-50 text-slate-700',
  },
  {
    title: 'Feedback',
    subtitle: 'Report issues or suggestions',
    to: '/feedback',
    icon: MessageSquare,
    tone: 'border-sky-100 bg-sky-50 text-sky-700',
  },
];
const SHOWCASE_IMAGES = ['/images/1.jpeg', '/images/2.jpeg', '/images/3.jpeg', '/images/4.jpeg'] as const;
const SNAPSHOT_ITEMS = [
  { label: 'Primary focus', value: 'Tutors, coaching, books and stationery' },
  { label: 'Built for', value: 'Students, parents, teachers, institutes and shops' },
  { label: 'Why it works', value: 'Nearby discovery, trust and learning continuity' },
  { label: 'Experience', value: 'Mobile-friendly education workflows' },
] as const;
const SOS_HIGHLIGHTS = [
  { label: 'Rapid action', value: 'Call, SMS, and WhatsApp-ready alerts' },
  { label: 'Location sharing', value: 'Capture live coordinates before sending help requests' },
  { label: 'Women safety focus', value: 'Built for female students and tutors inside their workflow' },
] as const;

const MobileServiceLauncher: React.FC<{ isSignedIn: boolean }> = ({ isSignedIn }) => {
  if (isSignedIn) return null;

  return (
    <section className="mx-auto max-w-7xl px-4 pb-3 pt-4 sm:px-6 lg:hidden">
      <div className="flex items-end justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Choose a service</p>
          <h2 className="mt-1 text-xl font-semibold text-slate-950">What do you want to use?</h2>
        </div>
        <Link to="/education" className="text-sm font-semibold text-cyan-700">
          Explore
        </Link>
      </div>
      <div className="mt-4 grid grid-cols-2 gap-3">
        {SERVICE_MODULES.map((item) => {
          const Icon = item.icon;
          const target = item.authRequired ? '/login' : item.to;
          return (
            <Link
              key={item.title}
              to={target}
              className="min-h-[128px] rounded-2xl border border-slate-200 bg-white p-3 shadow-sm transition active:scale-[0.98]"
            >
              <span className={`inline-flex h-10 w-10 items-center justify-center rounded-xl border ${item.tone}`}>
                <Icon className="h-5 w-5" aria-hidden="true" />
              </span>
              <span className="mt-3 block text-sm font-semibold text-slate-950">{item.title}</span>
              <span className="mt-1 block text-xs leading-5 text-slate-500">{item.subtitle}</span>
            </Link>
          );
        })}
      </div>
    </section>
  );
};

const getFeedId = (feed: Feed) => String(feed.id || feed._id || '').trim();
const getAuthorName = (feed: Feed) => {
  if (feed.createdByName && feed.createdByName.trim()) return feed.createdByName.trim();
  if (feed.source === 'SYSTEM') return 'System Update';
  return 'Community Member';
};
const getRoleLabel = (role?: Feed['createdByRole']) => {
  if (!role) return '';
  if (role === 'TEACHER') return 'Teacher';
  if (role === 'STUDENT') return 'Student';
  if (role === 'ADMIN') return 'Admin';
  return role;
};
const resolveErrorMessage = (error: unknown, fallback: string) => {
  if (!error || typeof error !== 'object') return fallback;
  const response = (error as { response?: { data?: any } }).response;
  const message = response?.data?.message;
  if (typeof message === 'string' && message.trim()) return message;
  if (Array.isArray(message) && message.length > 0) return String(message[0]);
  return fallback;
};

const Home: React.FC = () => {
  const { user } = useAuth();
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [activeSnapshotIndex, setActiveSnapshotIndex] = useState(0);
  const [feeds, setFeeds] = useState<Feed[]>([]);
  const [feedsLoading, setFeedsLoading] = useState(false);
  const [feedsError, setFeedsError] = useState<string | null>(null);
  const [feedsHasMore, setFeedsHasMore] = useState(true);
  const [feedsLoadingMore, setFeedsLoadingMore] = useState(false);
  const [feedStateFilter, setFeedStateFilter] = useState('');
  const [feedRoleFilter, setFeedRoleFilter] = useState<'ALL' | 'TEACHER' | 'STUDENT' | 'ADMIN'>('ALL');
  const [feedContentFilter, setFeedContentFilter] = useState<'ALL' | 'MEDIA' | 'TAGGED' | 'PINNED'>('ALL');
  const [feedSearch, setFeedSearch] = useState('');
  const [creatingFeed, setCreatingFeed] = useState(false);
  const [deletingFeedId, setDeletingFeedId] = useState<string | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createTitle, setCreateTitle] = useState('');
  const [createMessage, setCreateMessage] = useState('');
  const [createState, setCreateState] = useState('');
  const [createTags, setCreateTags] = useState('');
  const [createMediaUrls, setCreateMediaUrls] = useState('');
  const [createFiles, setCreateFiles] = useState<File[]>([]);
  const [followOverrides, setFollowOverrides] = useState<Record<string, { isFollowing: boolean; followersCount?: number }>>({});
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const createTitleInputRef = useRef<HTMLInputElement | null>(null);
  const createFormRef = useRef<HTMLFormElement | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  const currentUser: any = (user as any)?.user ? (user as any).user : user;
  const userRole = String(currentUser?.role || '').toUpperCase();
  const showFeeds = !!user && ['STUDENT', 'TEACHER', 'ADMIN'].includes(userRole);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setActiveImageIndex((prev) => (prev + 1) % SHOWCASE_IMAGES.length);
    }, 2200);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    let isMounted = true;
    if (!showFeeds) {
      setFeeds([]);
      setFeedsError(null);
      return undefined;
    }

    setFeedsLoading(true);
    setFeedsError(null);

    feedService
      .listFeeds({ limit: FEED_PAGE_SIZE, skip: 0, state: feedStateFilter || undefined })
      .then((data) => {
        if (!isMounted) return;
        const next = Array.isArray(data) ? data : [];
        setFeeds(next);
        setFeedsHasMore(next.length === FEED_PAGE_SIZE);
      })
      .catch((error) => {
        if (!isMounted) return;
        console.error('Failed to load feeds', error);
        setFeedsError('Unable to load updates right now.');
      })
      .finally(() => {
        if (!isMounted) return;
        setFeedsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [feedStateFilter, showFeeds]);

  const loadMoreFeeds = useCallback(async () => {
    if (feedsLoadingMore || feedsLoading || !feedsHasMore) return;
    setFeedsLoadingMore(true);
    try {
      const data = await feedService.listFeeds({
        limit: FEED_PAGE_SIZE,
        skip: feeds.length,
        state: feedStateFilter || undefined,
      });
      const next = Array.isArray(data) ? data : [];
      setFeeds((prev) => [...prev, ...next]);
      setFeedsHasMore(next.length === FEED_PAGE_SIZE);
    } catch (error) {
      console.error('Failed to load more feeds', error);
      setFeedsHasMore(false);
    } finally {
      setFeedsLoadingMore(false);
    }
  }, [feedStateFilter, feedsLoadingMore, feedsLoading, feedsHasMore, feeds.length]);

  useEffect(() => {
    if (!showFeeds || !feedsHasMore) return undefined;

    const node = loadMoreRef.current;
    if (!node) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry.isIntersecting || feedsLoadingMore || feedsLoading) return;
        loadMoreFeeds();
      },
      { rootMargin: '200px' },
    );

    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [showFeeds, feedsHasMore, feedsLoadingMore, feedsLoading, loadMoreFeeds]);

  useEffect(() => {
    if (!showCreateForm) return;

    createFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    const focusTimer = window.setTimeout(() => {
      createTitleInputRef.current?.focus();
    }, 250);

    return () => window.clearTimeout(focusTimer);
  }, [showCreateForm]);

  const submitFeed = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!createTitle.trim() || !createMessage.trim()) {
      setCreateError('Title and message are required.');
      return;
    }

    setCreatingFeed(true);
    setCreateError(null);

    try {
      const uploadResult = createFiles.length > 0
        ? await feedService.uploadMedia(createFiles)
        : { fileIds: [], urls: [] };
      const payload = {
        title: createTitle.trim(),
        message: createMessage.trim(),
        state: createState.trim() || undefined,
        tags: createTags
          .split(',')
          .map((tag) => tag.trim())
          .filter(Boolean),
        mediaUrls: createMediaUrls
          .split(',')
          .map((url) => url.trim())
          .filter(Boolean),
        mediaFileIds: uploadResult.fileIds,
      };
      const createdFeed = await feedService.createFeed(payload);

      setFeeds((prev) => [createdFeed, ...prev].slice(0, FEED_PAGE_SIZE));
      setCreateTitle('');
      setCreateMessage('');
      setCreateState('');
      setCreateTags('');
      setCreateMediaUrls('');
      setCreateFiles([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      setFeedsHasMore(true);
      setShowCreateForm(false);
    } catch (error) {
      console.error('Failed to create feed', error);
      setCreateError(resolveErrorMessage(error, 'Unable to post update right now.'));
    } finally {
      setCreatingFeed(false);
    }
  };

  const availableStates = useMemo(
    () =>
      Array.from(
        new Set(
          feeds
            .map((feed) => String(feed.state || '').trim())
            .filter(Boolean),
        ),
      ).sort((left, right) => left.localeCompare(right)),
    [feeds],
  );

  const filteredFeeds = useMemo(
    () =>
      feeds.filter((feed) => {
        const matchesRole = feedRoleFilter === 'ALL' || feed.createdByRole === feedRoleFilter;
        const mergedMediaUrls = [
          ...(feed.mediaUrls || []),
          ...(feed.mediaFileIds || []).map((id) => `${API_URL}/feeds/media/${id}`),
        ];
        const matchesContent =
          feedContentFilter === 'ALL' ||
          (feedContentFilter === 'MEDIA' && mergedMediaUrls.length > 0) ||
          (feedContentFilter === 'TAGGED' && (feed.tags?.length || 0) > 0) ||
          (feedContentFilter === 'PINNED' && !!feed.isPinned);
        const normalizedSearch = feedSearch.trim().toLowerCase();
        const matchesSearch =
          !normalizedSearch ||
          [
            feed.title,
            feed.message,
            feed.state,
            feed.createdByName,
            ...(feed.tags || []),
          ]
            .filter(Boolean)
            .some((value) => String(value).toLowerCase().includes(normalizedSearch));

        return matchesRole && matchesContent && matchesSearch;
      }),
    [feedContentFilter, feedRoleFilter, feedSearch, feeds],
  );

  const totalReactions = useMemo(
    () =>
      filteredFeeds.reduce(
        (sum, feed) => sum + (feed.likeCount ?? feed.likedBy?.length ?? 0) + (feed.dislikeCount ?? feed.dislikedBy?.length ?? 0),
        0,
      ),
    [filteredFeeds],
  );

  const handleDeleteFeed = useCallback(async (feedId: string) => {
    if (!feedId || deletingFeedId === feedId) return;
    const confirmed = window.confirm('Delete this post?');
    if (!confirmed) return;
    try {
      setDeletingFeedId(feedId);
      await feedService.deleteFeed(feedId);
      setFeeds((prev) => prev.filter((item) => getFeedId(item) !== feedId));
    } catch (error) {
      console.error('Failed to delete feed', error);
      window.alert(resolveErrorMessage(error, 'Unable to delete this post right now.'));
    } finally {
      setDeletingFeedId((current) => (current === feedId ? null : current));
    }
  }, [deletingFeedId]);

  const handleToggleFollow = useCallback(
    async (feedId: string, createdById: string, isFollowingAuthor: boolean) => {
      if (!feedId || !createdById) return;
      try {
        const response = isFollowingAuthor
          ? await feedService.unfollowUser(createdById)
          : await feedService.followUser(createdById);
        setFollowOverrides((prev) => ({
          ...prev,
          [feedId]: {
            isFollowing: response.isFollowing,
            followersCount: response.followersCount,
          },
        }));
      } catch (error) {
        console.error('Failed to update follow status', error);
      }
    },
    [],
  );

  const handleToggleLike = useCallback(async (feedId: string) => {
    if (!feedId) return;
    try {
      const updated = await feedService.toggleLike(feedId);
      setFeeds((prev) => prev.map((item) => (getFeedId(item) === feedId ? updated : item)));
    } catch (error) {
      console.error('Failed to like feed', error);
    }
  }, []);

  const handleToggleDislike = useCallback(async (feedId: string) => {
    if (!feedId) return;
    try {
      const updated = await feedService.toggleDislike(feedId);
      setFeeds((prev) => prev.map((item) => (getFeedId(item) === feedId ? updated : item)));
    } catch (error) {
      console.error('Failed to dislike feed', error);
    }
  }, []);

  const feedCards = useMemo(
    () =>
      filteredFeeds.map((feed) => {
        const feedId = getFeedId(feed);
        const key = feedId || `${feed.title}-${feed.createdAt || 'feed'}`;
        const isLiked = !!user?.id && (feed.likedBy || []).includes(user.id);
        const isDisliked = !!user?.id && (feed.dislikedBy || []).includes(user.id);
        const authorName = getAuthorName(feed);
        const authorRole = getRoleLabel(feed.createdByRole);
        const showFollow = !!feed.createdById && !!user?.id && feed.createdById !== user.id;
        const canDelete = !!feed.createdById && !!user?.id && feed.createdById === user.id;
        const followOverride = feedId ? followOverrides[feedId] : undefined;
        const isFollowingAuthor = followOverride?.isFollowing ?? !!feed.isFollowingAuthor;
        const followersCount = followOverride?.followersCount ?? feed.followersCount;
        const followingCount = feed.followingCount;
        const mergedMediaUrls = [
          ...(feed.mediaUrls || []),
          ...(feed.mediaFileIds || []).map((id) => `${API_URL}/feeds/media/${id}`),
        ];

        return (
          <article
            key={key}
            className="overflow-hidden rounded-[1.75rem] border border-slate-200 bg-white p-5 shadow-[0_24px_80px_-52px_rgba(15,23,42,0.45)] sm:p-6"
          >
            <div className="flex flex-col gap-4 border-b border-slate-200 pb-5 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex items-start gap-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-950 text-sm font-semibold text-white">
                  {authorName
                    .split(' ')
                    .filter(Boolean)
                    .slice(0, 2)
                    .map((part) => part[0]?.toUpperCase() || '')
                    .join('') || 'CM'}
                </div>
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-base font-semibold text-slate-950">{authorName}</p>
                    <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                      {authorRole || 'Member'}
                    </span>
                    {feed.isPinned && (
                      <span className="rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-amber-700">
                        Pinned
                      </span>
                    )}
                  </div>
                  <p className="mt-2 text-sm text-slate-500">
                    {feed.createdAt ? new Date(feed.createdAt).toLocaleDateString() : 'Recent update'}
                  </p>
                  {(typeof followersCount === 'number' || typeof followingCount === 'number') && (
                    <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-slate-500">
                      {typeof followersCount === 'number' && <span>{followersCount} followers</span>}
                      {typeof followingCount === 'number' && <span>{followingCount} following</span>}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {canDelete && (
                  <button
                    type="button"
                    onClick={() => {
                      void handleDeleteFeed(feedId);
                    }}
                    disabled={deletingFeedId === feedId}
                    className="inline-flex items-center rounded-full border border-rose-200 bg-rose-50 px-3 py-1.5 text-xs font-semibold text-rose-700 transition hover:bg-rose-100 disabled:opacity-60"
                  >
                    {deletingFeedId === feedId ? 'Deleting...' : 'Delete'}
                  </button>
                )}
                {showFollow && feedId && feed.createdById && (
                  <button
                    type="button"
                    onClick={() => {
                      void handleToggleFollow(feedId, feed.createdById as string, isFollowingAuthor);
                    }}
                    className={`inline-flex items-center rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
                      isFollowingAuthor
                        ? 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                        : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    {isFollowingAuthor ? 'Following' : 'Follow'}
                  </button>
                )}
              </div>
            </div>
            <div className="mt-5">
              <h3 className="text-2xl font-semibold tracking-tight text-slate-950">{feed.title}</h3>
              <p className="mt-3 text-sm leading-7 text-slate-600">{feed.message}</p>
            </div>
            {mergedMediaUrls.length > 0 && (
              <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
                {mergedMediaUrls.map((url) => {
                  const lower = url.toLowerCase();
                  const isVideo = lower.endsWith('.mp4') || lower.endsWith('.webm') || lower.endsWith('.ogg');
                  return isVideo ? (
                    <video key={url} controls className="w-full rounded-[1.25rem] border border-slate-200 bg-slate-950">
                      <source src={url} />
                    </video>
                  ) : (
                    <img
                      key={url}
                      src={url}
                      alt="Feed media"
                      className="h-full w-full rounded-[1.25rem] border border-slate-200 object-cover"
                    />
                  );
                })}
              </div>
            )}
            <div className="mt-5 flex flex-wrap items-center gap-2">
              {feed.state && (
                <span className="rounded-full border border-sky-200 bg-sky-50 px-3 py-1 text-xs font-semibold text-sky-700">{feed.state}</span>
              )}
              {feed.tags?.map((tag) => (
                <span key={tag} className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-600">
                  #{tag}
                </span>
              ))}
            </div>
            <div className="mt-5 flex flex-wrap items-center gap-3 border-t border-slate-200 pt-5 text-sm">
              <button
                type="button"
                className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 font-semibold transition ${
                  isLiked ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                }`}
                onClick={() => {
                  void handleToggleLike(feedId);
                }}
              >
                <HandThumbUpIcon className="h-4 w-4" aria-hidden="true" /> {feed.likeCount ?? (feed.likedBy?.length || 0)}
              </button>
              <button
                type="button"
                className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 font-semibold transition ${
                  isDisliked ? 'border-rose-200 bg-rose-50 text-rose-700' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                }`}
                onClick={() => {
                  void handleToggleDislike(feedId);
                }}
              >
                <HandThumbDownIcon className="h-4 w-4" aria-hidden="true" /> {feed.dislikeCount ?? (feed.dislikedBy?.length || 0)}
              </button>
            </div>
          </article>
        );
      }),
    [deletingFeedId, filteredFeeds, followOverrides, handleDeleteFeed, handleToggleDislike, handleToggleFollow, handleToggleLike, user?.id],
  );

  if (showFeeds) {
    return (
      <div className="min-h-screen bg-[radial-gradient(circle_at_top_left,_rgba(56,189,248,0.18),_transparent_22%),radial-gradient(circle_at_85%_18%,_rgba(251,146,60,0.16),_transparent_18%),linear-gradient(180deg,_#f8fafc_0%,_#ecfeff_46%,_#f8fafc_100%)]">
        <HomeSEO />
        <MobileServiceLauncher isSignedIn={!!user} />
        <section className="mx-auto max-w-7xl px-4 pb-6 pt-8 sm:px-6 lg:px-8 lg:pt-12">
          <div className="grid gap-6 xl:grid-cols-[minmax(0,1.05fr)_360px]">
            <div className="overflow-hidden rounded-[2rem] border border-slate-200 bg-[linear-gradient(135deg,_#020617_0%,_#0f172a_54%,_#0f766e_100%)] px-6 py-8 text-white shadow-[0_32px_90px_-52px_rgba(15,23,42,0.7)] sm:px-8 lg:px-10">
              <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
                <div>
                  <span className="inline-flex rounded-full border border-white/15 bg-white/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.26em] text-sky-100">
                    Community Feed
                  </span>
                  <h2 className="mt-5 max-w-3xl text-4xl font-semibold tracking-tight sm:text-5xl">
                    One place for announcements, wins, questions, and momentum across the platform.
                  </h2>
                  <p className="mt-4 max-w-2xl text-sm leading-7 text-slate-200 sm:text-base">
                    Home now acts as the product pulse. Post updates, follow educators and learners, and keep the community visible without leaving the main landing surface.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateForm((prev) => !prev);
                  }}
                  className="inline-flex items-center justify-center rounded-full bg-white px-5 py-3 text-sm font-semibold text-slate-950 transition hover:bg-slate-100"
                >
                  {showCreateForm ? 'Close composer' : 'Create new post'}
                </button>
              </div>

              <div className="mt-8 grid gap-3 sm:grid-cols-3">
                {[
                  { label: 'Visible updates', value: String(feeds.length) },
                  { label: 'Community reactions', value: String(totalReactions) },
                  { label: 'Feed mode', value: feedsHasMore ? 'Live and growing' : 'All caught up' },
                ].map((item) => (
                  <div key={item.label} className="rounded-[1.5rem] border border-white/12 bg-white/10 px-4 py-4 backdrop-blur">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-sky-100">{item.label}</p>
                    <p className="mt-3 text-2xl font-semibold text-white">{item.value}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-4">
              <div className="rounded-[1.75rem] border border-slate-200 bg-white p-5 shadow-[0_24px_80px_-52px_rgba(15,23,42,0.45)]">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Posting guide</p>
                <div className="mt-4 space-y-3">
                  {[
                    'Share class updates, achievements, events, or useful announcements.',
                    'Use media only when it adds context, not just decoration.',
                    'Keep titles short and make the message concrete.',
                  ].map((item) => (
                    <div key={item} className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
                      {item}
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-[1.75rem] border border-sky-200 bg-sky-50 p-5">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-sky-700">Why this matters</p>
                <p className="mt-3 text-sm leading-6 text-sky-900">
                  The feed should feel like a living community surface, not a raw database dump. This redesign gives it hierarchy, rhythm, and context inside the home page.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 pb-12 sm:px-6 lg:px-8">
          <div className="grid gap-6 xl:grid-cols-[minmax(0,1.1fr)_320px]">
            <div>
              <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Community stream</p>
                  <p className="mt-2 text-2xl font-semibold tracking-tight text-slate-950 sm:text-3xl">
                    Recent updates from teachers, students, and system activity.
                  </p>
                </div>
                <div className="rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-600 shadow-sm">
                  Showing {filteredFeeds.length} of {feeds.length} updates
                </div>
              </div>

              <div className="mb-6 grid grid-cols-1 gap-3 rounded-[1.5rem] border border-slate-200 bg-white p-4 shadow-[0_16px_48px_-38px_rgba(15,23,42,0.45)] sm:grid-cols-2 xl:grid-cols-5">
                <label className="flex min-w-0 flex-col gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                  State
                  <select
                    value={feedStateFilter}
                    onChange={(event) => setFeedStateFilter(event.target.value)}
                    className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-medium normal-case tracking-normal text-slate-900 outline-none transition focus:border-slate-400 focus:bg-white"
                  >
                    <option value="">All states</option>
                    {availableStates.map((state) => (
                      <option key={state} value={state}>
                        {state}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="flex min-w-0 flex-col gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                  Role
                  <select
                    value={feedRoleFilter}
                    onChange={(event) => setFeedRoleFilter(event.target.value as typeof feedRoleFilter)}
                    className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-medium normal-case tracking-normal text-slate-900 outline-none transition focus:border-slate-400 focus:bg-white"
                  >
                    <option value="ALL">All roles</option>
                    <option value="TEACHER">Teachers</option>
                    <option value="STUDENT">Students</option>
                    <option value="ADMIN">Admins</option>
                  </select>
                </label>

                <label className="flex min-w-0 flex-col gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                  Content
                  <select
                    value={feedContentFilter}
                    onChange={(event) => setFeedContentFilter(event.target.value as typeof feedContentFilter)}
                    className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-medium normal-case tracking-normal text-slate-900 outline-none transition focus:border-slate-400 focus:bg-white"
                  >
                    <option value="ALL">Everything</option>
                    <option value="MEDIA">Media posts</option>
                    <option value="TAGGED">Tagged posts</option>
                    <option value="PINNED">Pinned posts</option>
                  </select>
                </label>

                <label className="flex min-w-0 flex-col gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-slate-500 sm:col-span-2 xl:col-span-2">
                  Search
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <input
                      value={feedSearch}
                      onChange={(event) => setFeedSearch(event.target.value)}
                      placeholder="Search title, message, author, or tag"
                      className="min-w-0 flex-1 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-medium normal-case tracking-normal text-slate-900 outline-none transition focus:border-slate-400 focus:bg-white"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setFeedStateFilter('');
                        setFeedRoleFilter('ALL');
                        setFeedContentFilter('ALL');
                        setFeedSearch('');
                      }}
                      className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold normal-case tracking-normal text-slate-700 transition hover:bg-slate-50 sm:w-auto"
                    >
                      Reset
                    </button>
                  </div>
                </label>
              </div>

            {showCreateForm && (
              <form
                ref={createFormRef}
                onSubmit={submitFeed}
                className="mb-6 rounded-[1.75rem] border border-slate-200 bg-white p-5 shadow-[0_24px_80px_-52px_rgba(15,23,42,0.45)] sm:p-6"
              >
                <div className="flex flex-col gap-3 border-b border-slate-200 pb-5 sm:flex-row sm:items-end sm:justify-between">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Create update</p>
                    <h3 className="mt-2 text-2xl font-semibold tracking-tight text-slate-950">Publish something worth reading</h3>
                  </div>
                  <p className="max-w-sm text-sm text-slate-500">
                    Short title, clear context, and optional media. The post appears in the shared community feed.
                  </p>
                </div>
                <div className="mt-5 grid grid-cols-1 gap-4 lg:grid-cols-2">
                  <div className="flex flex-col gap-2">
                    <label className="text-sm font-semibold text-slate-700" htmlFor="feed-title">
                      Title
                    </label>
                    <input
                      id="feed-title"
                      ref={createTitleInputRef}
                      className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-slate-400 focus:bg-white"
                      placeholder="Add a short title"
                      value={createTitle}
                      onChange={(event) => setCreateTitle(event.target.value)}
                      disabled={creatingFeed}
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="text-sm font-semibold text-slate-700" htmlFor="feed-state">
                      State (optional)
                    </label>
                    <input
                      id="feed-state"
                      className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-slate-400 focus:bg-white"
                      placeholder="Telangana, Andhra Pradesh"
                      value={createState}
                      onChange={(event) => setCreateState(event.target.value)}
                      disabled={creatingFeed}
                    />
                  </div>
                </div>
                <div className="mt-4 flex flex-col gap-2">
                  <label className="text-sm font-semibold text-slate-700" htmlFor="feed-message">
                    Message
                  </label>
                  <textarea
                    id="feed-message"
                    className="min-h-[120px] rounded-[1.5rem] border border-slate-200 bg-slate-50 px-4 py-4 text-sm text-slate-900 outline-none transition focus:border-slate-400 focus:bg-white"
                    placeholder="Share your update with the community"
                    value={createMessage}
                    onChange={(event) => setCreateMessage(event.target.value)}
                    disabled={creatingFeed}
                  />
                </div>
                <div className="mt-4 grid gap-4 lg:grid-cols-2">
                  <div className="flex flex-col gap-2">
                    <label className="text-sm font-semibold text-slate-700" htmlFor="feed-media">
                      Media URLs (optional)
                    </label>
                    <input
                      id="feed-media"
                      className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-slate-400 focus:bg-white"
                      placeholder="https://...jpg, https://...mp4"
                      value={createMediaUrls}
                      onChange={(event) => setCreateMediaUrls(event.target.value)}
                      disabled={creatingFeed}
                    />
                    <p className="text-xs text-slate-500">Use comma-separated image or video URLs.</p>
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="text-sm font-semibold text-slate-700" htmlFor="feed-tags">
                      Tags (optional)
                    </label>
                    <input
                      id="feed-tags"
                      className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-slate-400 focus:bg-white"
                      placeholder="exam, announcement, event"
                      value={createTags}
                      onChange={(event) => setCreateTags(event.target.value)}
                      disabled={creatingFeed}
                    />
                  </div>
                </div>
                <div className="mt-4 flex flex-col gap-2">
                  <label className="text-sm font-semibold text-slate-700" htmlFor="feed-files">
                    Upload photos or videos (optional)
                  </label>
                  <input
                    id="feed-files"
                    type="file"
                    multiple
                    accept="image/*,video/*"
                    ref={fileInputRef}
                    onChange={(event) => {
                      const files = Array.from(event.target.files || []);
                      setCreateFiles(files);
                    }}
                    disabled={creatingFeed}
                    className="text-sm text-slate-600 file:mr-3 file:rounded-full file:border-0 file:bg-slate-950 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-white"
                  />
                  <p className="text-xs text-slate-500">
                    Up to 6 files, 4 MB each. Large images are optimized before upload for production stability.
                  </p>
                </div>
                {createError && (
                  <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{createError}</div>
                )}
                <div className="mt-5 flex flex-wrap items-center gap-3 border-t border-slate-200 pt-5">
                  <button
                    type="submit"
                    className="inline-flex items-center rounded-full bg-slate-950 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:opacity-60"
                    disabled={creatingFeed}
                  >
                    {creatingFeed ? 'Posting...' : 'Post update'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowCreateForm(false)}
                    className="inline-flex items-center rounded-full border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-60"
                    disabled={creatingFeed}
                  >
                    Cancel
                  </button>
                  <span className="text-xs text-slate-500">Visible to teachers and students</span>
                </div>
              </form>
            )}

            {feedsLoading && (
              <div className="rounded-[1.75rem] border border-slate-200 bg-white p-6 text-sm text-slate-600 shadow-[0_24px_80px_-52px_rgba(15,23,42,0.45)]">
                Loading updates...
              </div>
            )}

            {!feedsLoading && feedsError && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 p-6 text-sm text-rose-700">
                {feedsError}
              </div>
            )}

            {!feedsLoading && !feedsError && feeds.length === 0 && (
              <div className="rounded-[1.75rem] border border-slate-200 bg-white p-6 text-sm text-slate-600 shadow-[0_24px_80px_-52px_rgba(15,23,42,0.45)]">
                No updates yet. Please check back soon.
              </div>
            )}

            {!feedsLoading && !feedsError && feeds.length > 0 && filteredFeeds.length === 0 && (
              <div className="rounded-[1.75rem] border border-slate-200 bg-white p-6 text-sm text-slate-600 shadow-[0_24px_80px_-52px_rgba(15,23,42,0.45)]">
                No updates match the current filters. Try clearing one or more filters.
              </div>
            )}

            {!feedsLoading && !feedsError && filteredFeeds.length > 0 && (
              <div className="space-y-5">
                {feedCards.length > 0 ? feedCards : feeds.map((feed) => {
                  const feedId = getFeedId(feed);
                  const key = feedId || `${feed.title}-${feed.createdAt || 'feed'}`;
                  const isLiked = !!user?.id && (feed.likedBy || []).includes(user.id);
                  const isDisliked = !!user?.id && (feed.dislikedBy || []).includes(user.id);
                  const authorName = getAuthorName(feed);
                  const authorRole = getRoleLabel(feed.createdByRole);
                  const showFollow = !!feed.createdById && !!user?.id && feed.createdById !== user.id;
                  const canDelete = !!feed.createdById && !!user?.id && feed.createdById === user.id;
                  const followOverride = feedId ? followOverrides[feedId] : undefined;
                  const isFollowingAuthor = followOverride?.isFollowing ?? !!feed.isFollowingAuthor;
                  const followersCount =
                    followOverride?.followersCount ?? feed.followersCount;
                  const followingCount = feed.followingCount;
                  const mergedMediaUrls = [
                    ...(feed.mediaUrls || []),
                    ...(feed.mediaFileIds || []).map((id) => `${API_URL}/feeds/media/${id}`),
                  ];
                  return (
                    <article
                      key={key}
                      className="overflow-hidden rounded-[1.75rem] border border-slate-200 bg-white p-5 shadow-[0_24px_80px_-52px_rgba(15,23,42,0.45)] sm:p-6"
                    >
                      <div className="flex flex-col gap-4 border-b border-slate-200 pb-5 sm:flex-row sm:items-start sm:justify-between">
                        <div className="flex items-start gap-4">
                          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-950 text-sm font-semibold text-white">
                            {authorName
                              .split(' ')
                              .filter(Boolean)
                              .slice(0, 2)
                              .map((part) => part[0]?.toUpperCase() || '')
                              .join('') || 'CM'}
                          </div>
                          <div>
                            <div className="flex flex-wrap items-center gap-2">
                              <p className="text-base font-semibold text-slate-950">{authorName}</p>
                              <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                                {authorRole || 'Member'}
                              </span>
                              {feed.isPinned && (
                                <span className="rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-amber-700">
                                  Pinned
                                </span>
                              )}
                            </div>
                            <p className="mt-2 text-sm text-slate-500">
                              {feed.createdAt ? new Date(feed.createdAt).toLocaleDateString() : 'Recent update'}
                            </p>
                            {(typeof followersCount === 'number' || typeof followingCount === 'number') && (
                              <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-slate-500">
                                {typeof followersCount === 'number' && (
                                  <span>{followersCount} followers</span>
                                )}
                                {typeof followingCount === 'number' && (
                                  <span>{followingCount} following</span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          {canDelete && (
                            <button
                              type="button"
                              onClick={async () => {
                                if (!feedId || deletingFeedId === feedId) return;
                                const confirmed = window.confirm('Delete this post?');
                                if (!confirmed) return;
                                try {
                                  setDeletingFeedId(feedId);
                                  await feedService.deleteFeed(feedId);
                                  setFeeds((prev) => prev.filter((item) => getFeedId(item) !== feedId));
                                } catch (error) {
                                  console.error('Failed to delete feed', error);
                                  window.alert(resolveErrorMessage(error, 'Unable to delete this post right now.'));
                                } finally {
                                  setDeletingFeedId((current) => (current === feedId ? null : current));
                                }
                              }}
                              disabled={deletingFeedId === feedId}
                              className="inline-flex items-center rounded-full border border-rose-200 bg-rose-50 px-3 py-1.5 text-xs font-semibold text-rose-700 transition hover:bg-rose-100 disabled:opacity-60"
                            >
                              {deletingFeedId === feedId ? 'Deleting...' : 'Delete'}
                            </button>
                          )}
                          {showFollow && (
                            <button
                              type="button"
                              onClick={async () => {
                                if (!feedId || !feed.createdById) return;
                                try {
                                  const response = isFollowingAuthor
                                    ? await feedService.unfollowUser(feed.createdById)
                                    : await feedService.followUser(feed.createdById);
                                  setFollowOverrides((prev) => ({
                                    ...prev,
                                    [feedId]: {
                                      isFollowing: response.isFollowing,
                                      followersCount: response.followersCount,
                                    },
                                  }));
                                } catch (error) {
                                  console.error('Failed to update follow status', error);
                                }
                              }}
                              className={`inline-flex items-center rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
                                isFollowingAuthor
                                  ? 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                                  : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                              }`}
                            >
                              {isFollowingAuthor ? 'Following' : 'Follow'}
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="mt-5">
                        <h3 className="text-2xl font-semibold tracking-tight text-slate-950">{feed.title}</h3>
                        <p className="mt-3 text-sm leading-7 text-slate-600">{feed.message}</p>
                      </div>
                      {mergedMediaUrls.length > 0 && (
                        <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
                          {mergedMediaUrls.map((url) => {
                            const lower = url.toLowerCase();
                            const isVideo =
                              lower.endsWith('.mp4') ||
                              lower.endsWith('.webm') ||
                              lower.endsWith('.ogg');
                            return isVideo ? (
                              <video
                                key={url}
                                controls
                                className="w-full rounded-[1.25rem] border border-slate-200 bg-slate-950"
                              >
                                <source src={url} />
                              </video>
                            ) : (
                              <img
                                key={url}
                                src={url}
                                alt="Feed media"
                                className="h-full w-full rounded-[1.25rem] border border-slate-200 object-cover"
                              />
                            );
                          })}
                        </div>
                      )}
                      <div className="mt-5 flex flex-wrap items-center gap-2">
                        {feed.state && (
                          <span className="rounded-full border border-sky-200 bg-sky-50 px-3 py-1 text-xs font-semibold text-sky-700">{feed.state}</span>
                        )}
                        {feed.tags?.map((tag) => (
                          <span key={tag} className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-600">
                            #{tag}
                          </span>
                        ))}
                      </div>
                      <div className="mt-5 flex flex-wrap items-center gap-3 border-t border-slate-200 pt-5 text-sm">
                        <button
                          type="button"
                          className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 font-semibold transition ${
                            isLiked ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                          }`}
                          onClick={async () => {
                            if (!feedId) return;
                            try {
                              const updated = await feedService.toggleLike(feedId);
                              setFeeds((prev) =>
                                prev.map((item) =>
                                  getFeedId(item) === feedId ? updated : item,
                                ),
                              );
                            } catch (error) {
                              console.error('Failed to like feed', error);
                            }
                          }}
                        >
                          <HandThumbUpIcon className="h-4 w-4" aria-hidden="true" /> {feed.likeCount ?? (feed.likedBy?.length || 0)}
                        </button>
                        <button
                          type="button"
                          className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 font-semibold transition ${
                            isDisliked ? 'border-rose-200 bg-rose-50 text-rose-700' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                          }`}
                          onClick={async () => {
                            if (!feedId) return;
                            try {
                              const updated = await feedService.toggleDislike(feedId);
                              setFeeds((prev) =>
                                prev.map((item) =>
                                  getFeedId(item) === feedId ? updated : item,
                                ),
                              );
                            } catch (error) {
                              console.error('Failed to dislike feed', error);
                            }
                          }}
                        >
                          <HandThumbDownIcon className="h-4 w-4" aria-hidden="true" /> {feed.dislikeCount ?? (feed.dislikedBy?.length || 0)}
                        </button>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}

            {feeds.length > 0 && (
              <div className="mt-6 flex items-center justify-center">
                <div ref={loadMoreRef} className="h-8 w-full" />
                {feedsLoadingMore && (
                  <div className="text-sm text-slate-500">Loading more updates...</div>
                )}
              </div>
            )}

            {feedsHasMore && !feedsLoadingMore && !feedsLoading && (
              <div className="mt-4 flex justify-center">
                <button
                  type="button"
                  onClick={loadMoreFeeds}
                  className="rounded-full border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                >
                  Load more
                </button>
              </div>
            )}
            </div>

            <aside className="space-y-4">
              <div className="rounded-[1.75rem] border border-slate-200 bg-white p-5 shadow-[0_24px_80px_-52px_rgba(15,23,42,0.45)]">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Feed quality</p>
                <div className="mt-4 space-y-3">
                  {['Clear title', 'Useful context', 'Relevant media', 'Accurate tags'].map((item) => (
                    <div key={item} className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-medium text-slate-700">
                      {item}
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-[1.75rem] border border-emerald-200 bg-emerald-50 p-5">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-700">Community signal</p>
                <p className="mt-3 text-3xl font-semibold text-emerald-950">{totalReactions}</p>
                <p className="mt-2 text-sm leading-6 text-emerald-900">
                  Combined likes and dislikes across the updates currently visible on this page.
                </p>
              </div>

              <div className="rounded-[1.75rem] border border-slate-200 bg-slate-950 p-5 text-white shadow-[0_24px_80px_-52px_rgba(15,23,42,0.55)]">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-sky-200">Support</p>
                <p className="mt-3 text-lg font-semibold">Want to send product feedback instead?</p>
                <p className="mt-2 text-sm leading-6 text-slate-300">
                  Use the dedicated feedback page for structured product suggestions and issue reporting.
                </p>
                <Link
                  to="/feedback"
                  className="mt-5 inline-flex items-center rounded-full bg-white px-4 py-2 text-sm font-semibold text-slate-950 transition hover:bg-slate-100"
                >
                  Open feedback page
                </Link>
              </div>
            </aside>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top_left,_rgba(14,165,233,0.12),_transparent_24%),radial-gradient(circle_at_bottom_right,_rgba(249,115,22,0.10),_transparent_20%),linear-gradient(180deg,_#f8fafc_0%,_#ecfeff_45%,_#eef2ff_100%)]">
      <HomeSEO />
      <MobileServiceLauncher isSignedIn={!!user} />
      <section className="mx-auto max-w-7xl px-4 pb-10 pt-8 sm:px-6 lg:px-8 lg:pt-12">
         <div
          className="mb-6 overflow-hidden rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-900 shadow-sm"
          aria-label={EXTENSION_NOTICE}
        >
          <div className="scholarship-marquee whitespace-nowrap">
            <span>{EXTENSION_NOTICE}</span>
            <span className="ml-12" aria-hidden="true">{EXTENSION_NOTICE}</span>
          </div>
        </div>
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1.1fr)_460px] lg:items-stretch"> 
          <div className="overflow-hidden rounded-[32px] border border-white/70 bg-[linear-gradient(135deg,_rgba(15,23,42,0.96)_0%,_rgba(30,41,59,0.94)_38%,_rgba(8,145,178,0.90)_100%)] px-5 py-8 text-white shadow-[0_30px_100px_rgba(15,23,42,0.16)] sm:px-8 sm:py-10 lg:px-10">
            <span className="inline-flex rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.22em] text-cyan-100">
              LocalAlly
            </span>
            <h1 className="mt-5 max-w-3xl text-4xl font-semibold tracking-tight sm:text-5xl lg:text-6xl">
              Find trusted tutors, coaching, books and stationery near you
            </h1>
            <p className="mt-5 max-w-2xl text-base text-slate-200 sm:text-lg">
              LocalAlly helps students and parents search locally by class, subject, board, exam category, teaching mode, budget, language and distance.
            </p>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Link
                to={user ? '/dashboard' : '/login'}
                className="inline-flex items-center justify-center rounded-2xl bg-white px-5 py-3 text-sm font-semibold text-slate-950 transition hover:bg-cyan-50 sm:text-base"
              >
                {user ? 'Go to dashboard' : 'Get started'}
              </Link>
              <Link
                to="/education/tutors"
                className="inline-flex items-center justify-center rounded-2xl border border-white/15 bg-white/10 px-5 py-3 text-sm font-semibold text-white transition hover:bg-white/15 sm:text-base"
              >
                Find tutors nearby
              </Link>
            </div>

            <div className="mt-10 grid gap-3 sm:grid-cols-3">
              {[
                { label: 'Education', value: 'Tutors, coaching, demos' },
                { label: 'Learning support', value: 'Exams, progress, scholarships' },
                { label: 'Books ecosystem', value: 'Books, shops, stationery' },
              ].map((item) => (
                <div key={item.label} className="rounded-2xl border border-white/10 bg-white/10 px-4 py-4 backdrop-blur">
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-cyan-100">{item.label}</p>
                  <p className="mt-2 text-sm text-slate-100">{item.value}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="grid gap-4">
            <div className="rounded-[30px] border border-white/70 bg-white/80 p-4 shadow-[0_24px_70px_rgba(15,23,42,0.08)] backdrop-blur sm:p-5">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Platform Snapshot</p>
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                {SNAPSHOT_ITEMS.map((item, index) => {
                  const isActive = activeSnapshotIndex === index;
                  return (
                    <button
                      key={item.label}
                      type="button"
                      onClick={() => setActiveSnapshotIndex(index)}
                      className={`rounded-2xl px-4 py-5 text-left transition ${
                        isActive
                          ? 'bg-slate-950 text-white shadow-[0_18px_35px_rgba(15,23,42,0.16)]'
                          : 'border border-slate-200 bg-slate-50 text-slate-900 hover:bg-slate-100'
                      }`}
                    >
                      <p className={`text-sm ${isActive ? 'text-slate-300' : 'text-slate-500'}`}>{item.label}</p>
                      <p className={`mt-2 font-semibold ${isActive ? 'text-xl text-white' : 'text-lg text-slate-900'}`}>
                        {item.value}
                      </p>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="overflow-hidden rounded-[30px] border border-white/70 bg-white/80 p-4 shadow-[0_24px_70px_rgba(15,23,42,0.08)] backdrop-blur sm:p-5">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Visual Highlights</p>
                  <h2 className="mt-2 text-xl font-semibold text-slate-900">What the platform looks like</h2>
                </div>
                <div className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-600">
                  Live carousel
                </div>
              </div>
              <div className="relative mt-4 h-[260px] overflow-hidden rounded-[24px] border border-slate-200 bg-slate-100 sm:h-[320px]">
                {SHOWCASE_IMAGES.map((imageSrc, index) => (
                  <img
                    key={imageSrc}
                    src={imageSrc}
                    alt={`Highlight ${index + 1}`}
                    className={`absolute inset-0 h-full w-full object-contain transition-opacity duration-700 ${
                      activeImageIndex === index ? 'opacity-100' : 'opacity-0'
                    }`}
                  />
                ))}
              </div>
              <div className="mt-4 flex justify-center gap-2">
                {SHOWCASE_IMAGES.map((_, index) => (
                  <button
                    key={`dot-${index}`}
                    type="button"
                    onClick={() => setActiveImageIndex(index)}
                    className={`h-2.5 w-2.5 rounded-full transition ${
                      activeImageIndex === index ? 'bg-cyan-500' : 'bg-slate-300'
                    }`}
                    aria-label={`Show highlight image ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8">
        <div className="mb-4 overflow-hidden rounded-[32px] border border-rose-200 bg-[linear-gradient(135deg,_rgba(255,241,242,0.98)_0%,_rgba(255,247,237,0.98)_52%,_rgba(255,255,255,0.98)_100%)] p-5 shadow-[0_24px_70px_rgba(190,24,93,0.08)] sm:p-8">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-3xl">
              <span className="inline-flex rounded-full border border-rose-200 bg-white px-3 py-1 text-xs font-semibold uppercase tracking-[0.22em] text-rose-700">
                Emergency SOS
              </span>
              <h2 className="mt-4 text-3xl font-semibold tracking-tight text-slate-950 sm:text-4xl">
                Safety support for female teachers and students is now part of the platform.
              </h2>
              <p className="mt-4 text-sm leading-7 text-slate-600 sm:text-base">
                Trusted contacts, live location capture, ready-to-share SOS messaging, and a loud browser alarm give tutors and learners a faster response path during unsafe situations.
              </p>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row">
              <Link
                to={user ? '/sos' : '/login'}
                className="inline-flex items-center justify-center rounded-2xl bg-rose-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-rose-700"
              >
                {user ? 'Open SOS hub' : 'Sign in for SOS access'}
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
              >
                Contact support
              </Link>
            </div>
          </div>

          <div className="mt-6 grid gap-3 md:grid-cols-3">
            {SOS_HIGHLIGHTS.map((item) => (
              <div key={item.label} className="rounded-[1.5rem] border border-white/80 bg-white/80 px-4 py-4 backdrop-blur">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-rose-600">{item.label}</p>
                <p className="mt-2 text-sm font-medium leading-6 text-slate-700">{item.value}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-[32px] border border-white/70 bg-white/80 p-5 shadow-[0_24px_70px_rgba(15,23,42,0.08)] backdrop-blur sm:p-8">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Core Journeys</p>
              <h2 className="mt-2 text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
                Education first, with books built into the learning journey.
              </h2>
            </div>
            <p className="max-w-xl text-sm text-slate-600 sm:text-base">
              Each section supports the promise in the education roadmap: find the right tutor, continue learning, manage progress, and solve book needs locally.
            </p>
          </div>
          <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
            {PROMO_BANNERS.map((banner) => (
              <div
                key={banner.id}
                className={`rounded-[28px] bg-gradient-to-br ${banner.bg} p-6 text-white shadow-[0_20px_55px_rgba(15,23,42,0.12)] sm:p-8`}
              >
                <h3 className="text-2xl font-semibold tracking-tight sm:text-3xl">{banner.title}</h3>
                <p className="mt-3 text-sm text-white/85 sm:text-base">{banner.subtitle}</p>
                <div className="mt-5 flex flex-wrap gap-2">
                  {banner.bullets.map((item) => (
                    <span key={item} className="rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.14em]">
                      {item}
                    </span>
                  ))}
                </div>
                <div className="mt-7">
                  <Link
                    to={banner.to}
                    className="inline-flex items-center rounded-2xl bg-white px-4 py-3 text-sm font-semibold text-slate-950 transition hover:bg-slate-50"
                  >
                    {banner.cta}
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="grid gap-6 lg:grid-cols-[320px_minmax(0,1fr)]">
          <div className="rounded-[30px] border border-slate-200 bg-slate-950 p-6 text-white shadow-[0_24px_70px_rgba(15,23,42,0.12)]">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-cyan-200">Why this product matters</p>
            <h2 className="mt-4 text-3xl font-semibold tracking-tight">
              A better way to find trusted education support locally.
            </h2>
            <p className="mt-4 text-sm leading-6 text-slate-300">
              The project works best when tutor discovery, books, assessments, trust, and communication live in one connected education experience.
            </p>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            {[
              {
                name: 'Nearby-first discovery',
                description: 'Students and parents can find tutors, coaching providers, book listings, and verified book shops based on area instead of browsing random lists.',
              },
              {
                name: 'Education and exams network',
                description: 'Tutor profiles, sessions, scholarships, and exam flows stay grounded in trust and local relevance.',
              },
              {
                name: 'Local shops and community updates',
                description: 'Books, stationery, announcements, achievements, and media-rich updates can live close to the people who need them.',
              },
              {
                name: 'Parent and progress visibility',
                description: 'Dashboards, progress signals, exams, reviews, and support flows keep learning accountable after the tutor is selected.',
              },
            ].map((feature, index) => (
              <div key={feature.name} className="rounded-[28px] border border-white/70 bg-white/80 p-5 shadow-[0_24px_70px_rgba(15,23,42,0.08)] backdrop-blur">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-cyan-50 text-sm font-semibold text-cyan-700">
                    0{index + 1}
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900">{feature.name}</h3>
                </div>
                <p className="mt-4 text-sm leading-6 text-slate-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

    </div>
  );
};

export default Home;
